import { useEffect, useState } from 'react';
import {
  apiErrorText,
  useBindAccumulationContractMutation,
  useGetSmetaInfoQuery,
  useImportAccumulationFromKsPackageMutation,
  useListKsPackagesQuery,
  useListSmetaContextOptionsQuery,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Label, Select } from '@shared/ui/Input';
import {
  ErrMargin,
  FieldSelect,
  FieldSelectWide,
  FiltersMargin,
  MutedMargin,
} from './styled';

interface AccumulationBindingPanelProps {
  ledgerId: string;
  initialContractId?: string | null;
  initialProjectId?: string | null;
  canWrite: boolean;
}

export function AccumulationBindingPanel({
  ledgerId,
  initialContractId,
  initialProjectId,
  canWrite,
}: AccumulationBindingPanelProps) {
  const [bindContractId, setBindContractId] = useState(initialContractId ?? '');
  const [bindProjectId, setBindProjectId] = useState(initialProjectId ?? '');
  const [selectedKsPackageId, setSelectedKsPackageId] = useState('');
  const [importNotice, setImportNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const smetaInfoQ = useGetSmetaInfoQuery();
  const ksPackagesAvailable = smetaInfoQ.data?.flags?.ks_package ?? false;
  const contextOptionsQ = useListSmetaContextOptionsQuery();
  const ksPackagesQ = useListKsPackagesQuery(undefined, { skip: !ksPackagesAvailable });
  const [bindContract, { isLoading: bindingContract }] = useBindAccumulationContractMutation();
  const [importFromKsPackage, { isLoading: importingKsPkg }] =
    useImportAccumulationFromKsPackageMutation();

  const contextContracts = contextOptionsQ.data?.contracts ?? [];
  const contextProjects = contextOptionsQ.data?.projects ?? [];

  useEffect(() => {
    setBindContractId(initialContractId ?? '');
    setBindProjectId(initialProjectId ?? '');
  }, [initialContractId, initialProjectId]);

  async function handleBindContract() {
    if (!ledgerId) return;
    setError(null);
    try {
      await bindContract({
        id: ledgerId,
        statut_contract_id: bindContractId || null,
        project_id: bindProjectId || null,
      }).unwrap();
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить привязку к договору/объекту'));
    }
  }

  async function handleImportFromKsPackage() {
    if (!ledgerId || !selectedKsPackageId) return;
    setError(null);
    setImportNotice(null);
    try {
      const res = await importFromKsPackage({
        id: ledgerId,
        package_id: selectedKsPackageId,
      }).unwrap();
      setImportNotice(
        `Импортировано строк в накопительную ленту: ${res.imported_entries} (расценок: ${res.imported_unit_rates}).`,
      );
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось импортировать позиции из пакета КС-3'));
    }
  }

  return (
    <>
      <FiltersMargin>
        <FieldSelect>
          <Label>Договор Статута (мост в Пакет КС и Прогресс)</Label>
          <Select
            value={bindContractId}
            onChange={(e) => setBindContractId(e.target.value)}
            disabled={!canWrite}
          >
            <option value="">— не привязан —</option>
            {contextContracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.contract_number ? `№ ${c.contract_number}` : 'договор б/н'} ·{' '}
                {(c.subject || c.counterparty || '').slice(0, 40)}
              </option>
            ))}
          </Select>
        </FieldSelect>
        <FieldSelect>
          <Label>Проект ПТО / объект</Label>
          <Select
            value={bindProjectId}
            onChange={(e) => setBindProjectId(e.target.value)}
            disabled={!canWrite}
          >
            <option value="">— не привязан —</option>
            {contextProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code ? `${p.code} · ` : ''}
                {p.name}
              </option>
            ))}
          </Select>
        </FieldSelect>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          disabled={bindingContract || !canWrite}
          onClick={handleBindContract}
        >
          {bindingContract ? 'Сохраняю…' : 'Сохранить привязку'}
        </Button>
      </FiltersMargin>
      {error && <ErrMargin>{error}</ErrMargin>}

      {ksPackagesAvailable && (
        <>
          <FiltersMargin>
            <FieldSelectWide>
              <Label>Импорт из выпущенного пакета КС-3 (без повторной загрузки Excel)</Label>
              <Select
                value={selectedKsPackageId}
                onChange={(e) => setSelectedKsPackageId(e.target.value)}
                disabled={!canWrite}
              >
                <option value="">— выберите пакет КС-3 —</option>
                {(ksPackagesQ.data?.items ?? []).map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    КС-3 №{pkg.ks3_no} ({pkg.period_from} — {pkg.period_to}) · {pkg.status}
                  </option>
                ))}
              </Select>
            </FieldSelectWide>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              disabled={importingKsPkg || !selectedKsPackageId || !canWrite}
              onClick={handleImportFromKsPackage}
            >
              {importingKsPkg ? 'Импортирую…' : 'Импортировать позиции в ленту'}
            </Button>
          </FiltersMargin>
          {importNotice && <MutedMargin>{importNotice}</MutedMargin>}
        </>
      )}
    </>
  );
}
